import { isNil } from 'lodash-es';
import { type KeyboardEvent as ReactKeyboardEvent } from 'react';
import { useIntl } from 'react-intl';

import { CrosshairIcon } from '@dynatrace/strato-icons';

import { INTERACTION_MODE } from '../../../core/components/interactions-overlay/types.js';
import { calculateInitialSelectionAreaPositions } from '../../../core/components/interactions-overlay/utils/calculate-initial-selection-area-positions.js';
import { reduceExploreCancelAll } from '../../../core/components/interactions-state-helpers/reducers/explore-reducers.js';
import {
  reduceInteractionMode,
  reduceSelectionAreaVariant,
} from '../../../core/components/interactions-state-helpers/reducers/mode-reducers.js';
import { useSynchronizedSelection } from '../../../core/components/shared-chart-interactions/hooks/useSynchronizedSelection.js';
import { ChartToolbarRenderer } from '../../../core/components/toolbar/ChartToolbarRenderer.js';
import { useToolbarContext } from '../../../core/components/toolbar/hooks/useToolbarContext.js';
import { useA11yToolbarTabIndex } from '../../../core/hooks/useA11yToolbarTabIndex.js';
import {
  useInteractionModeState,
  useSelectState,
  useSetState,
} from '../../hooks/store/selectors.js';
import { useChartInteractions } from '../../hooks/useChartInteractions.js';
import { useTimeseriesChartId } from '../../hooks/useTimeseriesChartId.js';
import { useTimeseriesOverlayRef } from '../../hooks/useTimeseriesOverlayRef.js';
import { useXScales } from '../../hooks/useXScale.js';
import { TIMESERIES_MESSAGES } from '../../messages/tooltips-text.js';
import { reduceExploreTimeseriesSelect } from '../../utils/state-reducers.js';

export const ExploreButton = () => {
  const intl = useIntl();
  const setState = useSetState();
  const tabIndex = useA11yToolbarTabIndex(INTERACTION_MODE.Explore);
  const { pan } = useChartInteractions();
  const overlayRef = useTimeseriesOverlayRef();
  const { updateLastItemSelected } = useToolbarContext();

  const { currentScale: xScale } = useXScales();
  const chartId = useTimeseriesChartId();

  const selectState = useSelectState();

  const { updateSelectionState, resetSelectionState } =
    useSynchronizedSelection();

  const interactionMode = useInteractionModeState();
  const isSelected = interactionMode === INTERACTION_MODE.Explore;

  const modePlacement = pan?.enabled ? 'menu' : 'auto';

  const createInitialSelectionArea = () => {
    if (!isNil(overlayRef.current)) {
      const bounds = overlayRef.current.getBoundingClientRect();

      const [start, end] = calculateInitialSelectionAreaPositions(bounds);

      setState(
        reduceExploreTimeseriesSelect({
          start,
          end,
          finished: true,
          accessible: true,
          variant: 'select',
          isKeyboardCreated: true,
          domainStart: xScale.invert(start.relative.x),
          domainEnd: xScale.invert(end.relative.x),
        }),
      );

      updateSelectionState?.({
        selection: [start, end],
        chartId,
        scale: xScale,
      });
    }
  };

  const handleKeyDown = (event: ReactKeyboardEvent) => {
    if (event.key === 'Enter') {
      createInitialSelectionArea();
    }
  };

  const handleClick = () => {
    if (interactionMode !== 'explore') {
      setState(reduceInteractionMode(INTERACTION_MODE.Explore));
    }

    if (selectState?.start && selectState?.end) {
      if (selectState?.isKeyboardCreated) {
        reduceSelectionAreaVariant('select');
      } else {
        resetSelectionState?.();
        setState(reduceExploreCancelAll);
      }
    }

    updateLastItemSelected(INTERACTION_MODE.Explore);
  };

  return (
    <ChartToolbarRenderer.Control
      prefixIcon={<CrosshairIcon />}
      tooltipText={intl.formatMessage(TIMESERIES_MESSAGES.zoomModeExplore)}
      tooltipKeyShortcut="E"
      selected={isSelected}
      onKeyDown={handleKeyDown}
      onClick={handleClick}
      tabIndex={tabIndex}
      placement={modePlacement}
    />
  );
};
ExploreButton['displayName'] = 'ExploreButton';
